import { Image } from "react-native"
import React, { useEffect, useState } from "react";
import type { CompetitionProps, CompetitionResultTypeProps, FocusPositionProps, MyPlayerProps, PublicPlayerProps } from "../../types";
import { ManageCompetitionApi } from "../api";
import { Icons, Toggle } from "../../Components";
import DropDown from "../../Components/Dropdown";
import moment from "moment-mini";
import { Button, Text, TextInput, View } from "../../Components/Themed";
import { useColors } from "../../constants/useColors";

type CompetitionSettingsFormProps = {
    player?: MyPlayerProps,
    competition: CompetitionProps,
    init_expanded?:boolean,
    onFocusPosition?:(position:FocusPositionProps) => void,
    competition_result_types:CompetitionResultTypeProps[],
    onShowPacers:() => void,
    onCompetitionUpdate:(c:CompetitionProps) => void
}

const market_types = ['FOR_MONEY', 'FREE']

const CompetitionSettingsForm = ({ init_expanded, onFocusPosition, player, competition, competition_result_types, onCompetitionUpdate, onShowPacers }: CompetitionSettingsFormProps) => {
    const [ expanded, setExpanded ] = useState(init_expanded ?? false);
    const [ draft_competition, setDraftCompetition ] = useState<CompetitionProps | undefined>(undefined);
    //const [ player_selector_visible, setPlayerSelectorVisible ] = useState(false);
    const [ pacer, setPacer ] = useState<PublicPlayerProps | MyPlayerProps>();
    const Colors = useColors();
    const competition_result_type = competition_result_types.find(crt => crt.competition_result_type_id == competition?.competition_result_type_id);

    useEffect(() => {
        setDraftCompetition(competition);
    },[competition])

    useEffect(() => {
        if(!competition?.pacer_id){ return }
        if(competition.pacer_id == pacer?.player_id){ return } //Already got him/her
        getPacerFromServer(competition.pacer_id)
    },[competition?.pacer_id])

    useEffect(() => {
        if(!competition_result_type){ return }
        if(competition_result_type.type == 'outpace' && !competition.pacer_id){
            setPacer(player)
            onCompetitionUpdate({ ...competition, pacer_id: player?.player_id })
        }
    },[competition_result_type?.type])

    const getPacerFromServer = async(player_id:string) => {
        const ps = await ManageCompetitionApi.getPlayersByPlayerIds([player_id])
        setPacer(ps[0])
    }


    if(!competition || !draft_competition){ return <></> }
    const is_changed = JSON.stringify(competition) != JSON.stringify(draft_competition) ? true : false
    const is_ticket_changed = draft_competition.buy_in.toString() != competition.buy_in.toString() ? true : false
    const is_guar_changed = draft_competition.guaranteed_payout?.toString() != competition.guaranteed_payout?.toString() ? true : false
    const is_max_tickets_changed = draft_competition.available_tickets.toString() != competition.available_tickets.toString() ? true : false
    return (
        <View float style={{ flexGrow:1, borderRadius:0 }}>
            <Button 
                disabled={init_expanded}
            float style={{ flexDirection:'row', alignItems:'center', borderRadius:0, padding:20, backgroundColor:Colors.toggle.button.active }} onPress={() => setExpanded(!expanded)}>
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1' color={Colors.toggle.text.active}>Competition Settings</Text>
                    <Text style={{ marginTop:3 }} theme='description' color={Colors.toggle.text.active}>Manage the high level information of this competition</Text>
                </View>
                {is_changed ?
                <Button
                    title='SAVE'
                    type='success'
                    style={{ padding:10 }}
                    onPress={() => onCompetitionUpdate(draft_competition)}
                />
                : !init_expanded ?
                <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} color={Colors.text.h1} size={8} />
                :<></>
                }
            </Button>
            {expanded ?
            <View type='body' nativeID="competition_settings" style={{ flexGrow:1 }}>
                
                <View nativeID="competition_currency" style={{ padding:20, borderBottomWidth:1, borderBottomColor:Colors.borders.light }}>
                    <View style={{  margin:5, minWidth:200 }}>
                        <Text theme="h1">Competition Currency</Text>
                        <Text style={{ marginTop:3 }} theme="description">Is the ticket price in real-money or edge coins?</Text>
                    </View>
                    <View style={{ justifyContent:'flex-end', margin: 5, borderRadius:8 }}>
                        <Toggle
                            options={market_types.map(t => {
                                return { key: t, label: t }
                            })}
                            onSelectOption={(key) => onCompetitionUpdate({ ...draft_competition, market_type: key})}
                            selected_option={competition.market_type}
                        />
                       
                    </View>
                </View>
                <View style={{borderBottomWidth:1, borderBottomColor:Colors.borders.light, padding:20}}>
                <View type='row' nativeID="competition_payout" style={{ flexWrap:'wrap' }}>
                    <View transparent style={{ flex:1, minWidth:200 }}>
                        <Text theme="h1">Competition Payout Type</Text>
                        <Text style={{ marginTop:3 }} theme="description">{competition_result_type?competition_result_type.description:'Choose the type of payout for this competition'}</Text>
                    </View>
                    <View transparent style={{ flex:1, justifyContent:'flex-end', minWidth:100, margin:5, borderRadius:8 }}>
                        <DropDown
                            read_only={competition.competition_season_id ? true : false}
                            dropdown_options={[
                                {value: 'competition_result_type_id', eligible_options: competition_result_types.sort((a,b) => a.label.localeCompare(b.label)).map(t => t.label)}
                            ]}
                            selected_value={competition_result_type?.label ?? ''}
                            onOptionSelect={(type) => {
                                const new_result_type = competition_result_types.find(crt => crt.label == type);
                                if(!new_result_type){ return }
                                onCompetitionUpdate({ ...draft_competition, competition_result_type_id: new_result_type.competition_result_type_id })
                            }}
                        />
                    </View>
                </View>
                {competition_result_type?.type == 'outpace' ?
                <View transparent style={{ marginTop:10 }}>
                    <Text style={{ marginBottom:10 }} theme="h1">Select a Pacer</Text>
                    <Button 
                        float
                        disabled={competition.competition_season_id ? true : false}
                        style={{ borderRadius:8, padding:10 }} onPress={() => onShowPacers()}>
                        {pacer ?
                        <View transparent type='row'>
                            <Icons.PacerCarIcon size={24} color={Colors.text.h1} />
                            <Image
                                source={{ uri: pacer.profile_pic && pacer.profile_pic != '' ? pacer.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                style={{ height:40, width:40, borderRadius:4, marginLeft:10 }}
                                resizeMode="cover"
                            />
                            <View transparent style={{ flex:1, marginLeft:10 }}>
                                <Text theme="h2">@{pacer.username}</Text>
                                <Text style={{ marginTop:3 }} theme="description">Joined {moment(pacer.create_datetime).format('MMM YYYY')}</Text>
                            </View>
                            <Icons.ChevronIcon direction="right" color={Colors.text.h1} size={8} />
                        </View>
                        :
                        <View type='row'>
                            <Icons.AlertIcon size={20} color={Colors.text.warning} />
                            <Image
                                source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                style={{ height:40, width:40, borderRadius:4, marginLeft:10 }}
                                resizeMode="cover"
                            />
                            <View style={{ flex:1, marginLeft:10 }}>
                                <Text theme="h2">Select A Pacer!</Text>
                            </View>
                            <Icons.ChevronIcon direction="right" color={Colors.text.h1} size={8} />
                        </View>
                        }
                    </Button>
                </View>
                :<></>}
                </View>
                <View type='row' nativeID="buy_in" style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:is_ticket_changed ? Colors.text.warning : Colors.borders.light }}>
                    <View style={{ flex:1, minWidth:200 }}>
                        <Text theme="h1">Ticket Price</Text>
                        <Text style={{ marginTop:3 }} theme="description">Set the amount required to purchase a ticket to participate</Text>
                    </View>
                    <TextInput
                        style={{ flexGrow:1, textAlign:'center', minWidth:100, margin:5, borderColor:competition.buy_in ? Colors.text.success: Colors.text.warning  }}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, buy_in: text })}
                        placeholder="0"
                        onFocus={() => {
                            if(draft_competition.buy_in == 0){ setDraftCompetition({ ...draft_competition, buy_in: '' }) }
                        }}
                        onFocusPosition={onFocusPosition}
                        keyboardType="decimal-pad"
                        editable={competition.competition_season_id ? false : true}
                        value={draft_competition.buy_in.toString()}
                    />
                    {is_ticket_changed ?
                    <Button
                        title='SAVE'
                        type='success'
                        style={{ margin:5, padding:15, flexGrow:1 }}
                        onPress={() => onCompetitionUpdate(draft_competition)}
                    />
                    :<></>}
                </View>
                {player?.role == 'admin' ?
                <View type='row' nativeID="buy_in" style={{ flexWrap:'wrap', padding:20,  borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ flexGrow:1, minWidth:100 }}>
                        <Text theme="h1">Guaranteed Payout</Text>
                        <Text style={{ marginTop:3 }} theme="description">ADMIN ONLY - How much is guaranteed to be paid out?</Text>
                    </View>
                    <TextInput
                        style={{ flexGrow:1, textAlign:'center', minWidth:100, margin:5 }}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, guaranteed_payout: text })}
                        placeholder="0"
                        onFocusPosition={onFocusPosition}
                        keyboardType="decimal-pad"
                        editable={competition.competition_season_id ? false : true}
                        value={draft_competition.guaranteed_payout?.toString()}
                    />
                    {is_guar_changed ?
                    <Button
                        title='SAVE'
                        type='success'
                        style={{ margin:5, padding:15, flexGrow:1 }}
                        onPress={() => onCompetitionUpdate(draft_competition)}
                    />
                    :<></>}
                </View>
                :<></>}
                    
                <View type='row' nativeID="Max Tickets" style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderBottomColor:Colors.borders.light }}>
                    <View style={{ flexGrow:1, marginRight:5, minWidth:100 }}>
                        <Text theme="h1">Max Tickets</Text>
                        <Text style={{ marginTop:3 }} theme="description">Maximum number of tickets that can be sold</Text>
                    </View>
                    <TextInput
                        style={{ flexGrow:1, textAlign:'center', minWidth:100, margin:5, borderColor:parseInt(competition.available_tickets as string) > 0 ? Colors.text.success : Colors.text.warning }}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, available_tickets: text })}
                        placeholder="100"
                        onFocus={() => {
                            if(draft_competition.available_tickets == 0){ setDraftCompetition({ ...draft_competition, available_tickets: '' }) }
                        }}
                        onFocusPosition={onFocusPosition}
                        keyboardType="decimal-pad"
                        editable={competition.competition_season_id ? false : true}
                        value={draft_competition.available_tickets.toString()}
                    />
                     {is_max_tickets_changed ?
                    <Button
                        title='SAVE'
                        type='success'
                        style={{ margin:5, padding:15, flexGrow:1 }}
                        onPress={() => onCompetitionUpdate(draft_competition)}
                    />
                    :<></>}
                </View>
            </View>
            :<></>}
        </View>
    )
}

export default CompetitionSettingsForm